import { Button, Result } from 'antd';
import React from 'react';
import { isChunkLoadError } from '@/utils/chunkRecovery';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  /** 本次异常是否属于「分包/chunk 加载失败」——文案与处置都不同 */
  isChunkError: boolean;
  errorKey: number; // 递增 key 用于销毁旧实例 + 刷新
}

/**
 * 全局 Error Boundary
 * 捕获子组件树渲染异常，防止一个页面报错导致整个布局崩溃
 *
 * ★ 2026-10-20：区分「渲染异常」与「chunk 加载失败」两种错误。
 *   重新部署后，旧 HTML 会去请求已被删除的旧哈希分包：`import()` 抛
 *   `ChunkLoadError` / `Failed to fetch dynamically imported module`，被 React.lazy
 *   重新抛进这里。此时「重试」没用（那个文件已经不存在），正确的处置是刷新页面
 *   拿新 HTML；给错文案会让用户反复点「重试」。
 *   运行期的自动自愈（不经过本组件的那条路）见 src/utils/chunkRecovery.ts。
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, isChunkError: false, errorKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      isChunkLoadError(error)
        ? '[ErrorBoundary] 分包加载失败（通常是本站已发布新版本）:'
        : '[ErrorBoundary] 页面渲染异常:',
      error,
      errorInfo,
    );
  }

  componentDidUpdate(_prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    if (prevState.hasError && !this.state.hasError) {
      // 错误恢复后递增 key，强制重新挂载子组件
      this.setState((s) => ({ errorKey: s.errorKey + 1 }));
    }
  }

  handleRefresh = () => {
    this.setState({ hasError: false, isChunkError: false });
  };

  render() {
    if (this.state.hasError) {
      const chunkError = this.state.isChunkError;
      return (
        <div style={{ padding: 100, textAlign: 'center' }}>
          <Result
            status="error"
            title={chunkError ? '页面资源已更新或加载失败' : '页面渲染异常'}
            subTitle={
              chunkError
                ? '本站可能刚刚发布了新版本，当前页面引用的旧资源已失效。点「刷新页面」加载最新版本即可继续。'
                : '当前页面发生了未知错误，请刷新页面后重试'
            }
            extra={
              chunkError
                ? [
                    <Button
                      type="primary"
                      key="reload"
                      onClick={() => window.location.reload()}
                    >
                      刷新页面
                    </Button>,
                    <Button key="retry" onClick={this.handleRefresh}>
                      重试
                    </Button>,
                  ]
                : [
                    <Button type="primary" key="refresh" onClick={this.handleRefresh}>
                      重试
                    </Button>,
                    <Button key="home" onClick={() => window.location.reload()}>
                      刷新页面
                    </Button>,
                  ]
            }
          />
        </div>
      );
    }

    return <React.Fragment key={this.state.errorKey}>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;